import type { FC, RefAttributes } from "react";
import { Slot } from "@/components/ui/composition-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";
import type { ComponentPropsWithoutRef, ReactNode, ComponentProps } from "react";

const Breadcrumb: FC<
  ComponentPropsWithoutRef<"nav"> & {
    separator?: ReactNode;
  } & RefAttributes<HTMLElement>
> = ({ ref, ...props }) => <nav ref={ref} aria-label="breadcrumb" {...props} />;
Breadcrumb.displayName = "Breadcrumb";

const BreadcrumbList: FC<ComponentPropsWithoutRef<"ol"> & RefAttributes<HTMLOListElement>> = ({
  ref,
  className,
  ...props
}) => (
  <ol
    ref={ref}
    className={cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
      className,
    )}
    {...props}
  />
);
BreadcrumbList.displayName = "BreadcrumbList";

const BreadcrumbItem: FC<ComponentPropsWithoutRef<"li"> & RefAttributes<HTMLLIElement>> = ({
  ref,
  className,
  ...props
}) => <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props} />;
BreadcrumbItem.displayName = "BreadcrumbItem";

const BreadcrumbLink: FC<
  ComponentPropsWithoutRef<"a"> & {
    asChild?: boolean;
  } & RefAttributes<HTMLAnchorElement>
> = ({ ref, asChild, className, ...props }) => {
  const Comp = asChild ? Slot : "a";

  return <Comp ref={ref} className={cn("transition-colors hover:text-foreground", className)} {...props} />;
};
BreadcrumbLink.displayName = "BreadcrumbLink";

const BreadcrumbPage: FC<ComponentPropsWithoutRef<"span"> & RefAttributes<HTMLSpanElement>> = ({
  ref,
  className,
  ...props
}) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn("font-normal text-foreground", className)}
    {...props}
  />
);
BreadcrumbPage.displayName = "BreadcrumbPage";

const BreadcrumbSeparator = ({ children, className, ...props }: ComponentProps<"li">) => (
  <li role="presentation" aria-hidden="true" className={cn("[&>svg]:size-3.5", className)} {...props}>
    {children ?? <ChevronRight />}
  </li>
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

const BreadcrumbEllipsis = ({ className, ...props }: ComponentProps<"span">) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("flex size-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="size-4" />
    <span className="sr-only">More</span>
  </span>
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
